import { useEffect, useRef, useState } from "react";
import { DEAL_AMOUNT_RANGE_PRESETS } from "../utils/dealsViewConstants";

interface DealAmountRangePopoverProps {
  value: string[];
  onChange: (vals: string[]) => void;
  currencySymbol: string;
}

export const DealAmountRangePopover = ({
  value,
  onChange,
  currencySymbol,
}: DealAmountRangePopoverProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [minVal, setMinVal] = useState(value?.[0] ?? "");
  const [maxVal, setMaxVal] = useState(value?.[1] ?? "");
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMinVal(value?.[0] ?? "");
    setMaxVal(value?.[1] ?? "");
  }, [value]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
        setMinVal(value?.[0] ?? "");
        setMaxVal(value?.[1] ?? "");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, value]);

  const handleApply = () => {
    const min = minVal.trim();
    const max = maxVal.trim();
    if (min && max) {
      onChange([min, max]);
    } else if (min) {
      onChange([min, ""]);
    } else if (max) {
      onChange(["", max]);
    } else {
      onChange([]);
    }
    setIsOpen(false);
  };

  const handleCancel = () => {
    setMinVal(value?.[0] ?? "");
    setMaxVal(value?.[1] ?? "");
    setIsOpen(false);
  };

  const handlePresetClick = (min: number, max: number) => {
    setMinVal(String(min));
    setMaxVal(String(max));
  };

  const displayLabel =
    value?.[0] && value?.[1]
      ? `${currencySymbol}${Number(value[0]).toLocaleString()} – ${currencySymbol}${Number(value[1]).toLocaleString()}`
      : value?.[0]
        ? `${currencySymbol}${Number(value[0]).toLocaleString()}+`
        : "";

  const formatPresetLabel = (min: number, max: number) =>
    `${currencySymbol}${min.toLocaleString()} – ${max.toLocaleString()}`;

  return (
    <div className="relative min-w-[150px] max-w-[150px]" ref={popoverRef}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="w-full h-[32px] bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-lg px-2 flex items-center gap-1 text-md cursor-pointer"
      >
        <span
          className={`truncate ${displayLabel ? "font-medium text-verba-indigo dark:text-verba-indigo-dk" : "text-gray-placeholder dark:text-gray-placeholder-dk"}`}
        >
          {displayLabel || "Select range"}
        </span>
      </button>

      {isOpen && (
        <div className="absolute top-[36px] right-0 z-50  bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-lg shadow-[10px_10px_60px_0px_rgba(0,0,0,0.2)] p-[18px] flex flex-col gap-6">
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2">
              <div className="flex-1 h-[36px] bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-lg px-2 flex items-center gap-0.5">
                <span className="text-md font-semibold text-verba-indigo dark:text-verba-indigo-dk shrink-0">
                  {currencySymbol}
                </span>
                <input
                  type="number"
                  value={minVal}
                  onChange={(e) => setMinVal(e.target.value)}
                  placeholder="Min"
                  className="flex-1 min-w-0 h-full bg-transparent text-md text-verba-indigo dark:text-verba-indigo-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>
              <div className="flex-1 h-[36px] bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-lg px-2 flex items-center gap-0.5">
                <span className="text-md font-semibold text-verba-indigo dark:text-verba-indigo-dk shrink-0">
                  {currencySymbol}
                </span>
                <input
                  type="number"
                  value={maxVal}
                  onChange={(e) => setMaxVal(e.target.value)}
                  placeholder="Max"
                  className="flex-1 min-w-0 h-full bg-transparent text-md text-verba-indigo dark:text-verba-indigo-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-1.5">
              {DEAL_AMOUNT_RANGE_PRESETS.map((preset) => {
                const isActive =
                  minVal === String(preset.min) &&
                  maxVal === String(preset.max);
                return (
                  <button
                    key={`${preset.min}-${preset.max}`}
                    type="button"
                    onClick={() => handlePresetClick(preset.min, preset.max)}
                    className={`px-1 py-0.5 rounded text-xxs font-semibold border cursor-pointer whitespace-nowrap transition-colors ${
                      isActive
                        ? "bg-crm-card-highlight-2 dark:bg-crm-card-highlight-2-dk border-crm-card-border dark:border-crm-card-border-dk text-verba-indigo dark:text-verba-indigo-dk"
                        : "border-border-gray dark:border-border-gray-dk text-verba-indigo dark:text-verba-indigo-dk hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                    }`}
                  >
                    {formatPresetLabel(preset.min, preset.max)}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={handleCancel}
              className="w-[120px] h-[26px] rounded-lg border border-verba-indigo dark:border-verba-indigo-dk text-md font-semibold text-verba-indigo dark:text-verba-indigo-dk cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApply}
              className="w-[120px] h-[26px] rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk text-md font-semibold text-pure-white dark:text-pure-white-dk cursor-pointer"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
